import { Star } from "lucide-react";
import { Eyebrow } from "@/components/Eyebrow";
import { SectionTitle } from "@/components/SectionTitle";
import { REVIEWS, REVIEWS_AGGREGATE_FALLBACK, type Review } from "@/data/reviews";
import { useGoogleReviewsStats } from "@/hooks/useSiteSettings";

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" role="img" aria-label={`${rating} stelle su 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          aria-hidden
          size={14}
          className={i < rating ? "fill-brand-gold text-brand-gold" : "text-brand-gold/25"}
        />
      ))}
    </div>
  );
}

function GoogleG({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.3 0-9.7-3.4-11.3-8.2l-6.5 5C9.6 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41 35.5 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}

// Nome e iniziale del cognome: basta a riconoscere la recensione su Google senza ripubblicare il nome intero.
function nomeBreve(author: string) {
  const parti = author.trim().split(/\s+/);
  if (parti.length < 2) return author;
  return `${parti[0]} ${parti[parti.length - 1].charAt(0).toUpperCase()}.`;
}

function ReviewCard({ r }: { r: Review }) {
  return (
    <article className="shrink-0 w-[280px] md:w-[340px] bg-brand-cream border border-brand-gold/20 rounded-md p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-brand-red/10 text-brand-red flex items-center justify-center font-display text-sm shrink-0">
            {r.author.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-display text-sm text-brand-charcoal truncate">{nomeBreve(r.author)}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-brand-warm-gray">{r.when}</div>
          </div>
        </div>
        <GoogleG size={18} />
      </div>
      <Stars rating={r.rating} />
      <p className="text-sm text-brand-charcoal/80 leading-relaxed line-clamp-4">{r.text}</p>
    </article>
  );
}

export function ReviewsMarquee({ className = "", compact = false }: { className?: string; compact?: boolean }) {
  const loop = [...REVIEWS, ...REVIEWS];
  const { rating, count } = useGoogleReviewsStats({
    rating: REVIEWS_AGGREGATE_FALLBACK.rating,
    count: REVIEWS_AGGREGATE_FALLBACK.count,
  });
  return (
    <section
      className={`bg-white ${compact ? "py-12" : "py-16 md:py-20"} ${className}`}
      aria-label="Recensioni clienti"
    >
      <div className="container mx-auto px-4 text-center mb-8">
        <Eyebrow>Dicono di noi</Eyebrow>
        <SectionTitle align="center" className="mt-3">Le recensioni dei nostri clienti</SectionTitle>
        <div className="mt-4 inline-flex items-center gap-3 text-sm text-brand-warm-gray">
          <GoogleG size={16} />
          <span className="flex items-center gap-1">
            <Stars rating={Math.round(rating)} />
            <strong className="text-brand-charcoal ml-1">{rating.toString().replace(".", ",")}</strong>
            <span>· {count} recensioni Google</span>
          </span>
        </div>
      </div>

      <div className="reviews-marquee group relative overflow-hidden">
        <div className="reviews-marquee-track flex gap-4 md:gap-5 w-max">
          {loop.map((r, i) => (
            <ReviewCard key={`${r.author}-${i}`} r={r} />
          ))}
        </div>
      </div>

      <div className="text-center mt-8">
        <a
          href={REVIEWS_AGGREGATE_FALLBACK.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-brand-red hover:underline"
        >
          Lascia una recensione su Google
        </a>
        <p className="mt-3 text-xs text-brand-warm-gray">
          Selezione di recensioni pubblicate su Google. Non verifichiamo che gli autori abbiano effettivamente acquistato da noi.
        </p>
      </div>
    </section>
  );
}